import { create } from "zustand";
import { LocationInput } from "@/types/custom";

export type ImageData = {
  file: File;
  preview: string;
  url?: string;
};

interface CustomActivityState {
  title: string;
  description: string;
  duration: string;
  cost: string;
  images: ImageData[];
  categories: string[];
  location: LocationInput;
  selectedOption: "library" | "private";
  uploadingImages: boolean;
  isGeneratingDescription: boolean;
  currentStepIndex: number;

  // Actions
  setTitle: (title: string) => void;
  setDescription: (description: string | ((prev: string) => string)) => void;
  setDuration: (duration: string) => void;
  setCost: (cost: string) => void;
  addImage: (image: ImageData) => void;
  removeImage: (index: number) => void;
  setCategories: (categories: string[]) => void;
  toggleCategory: (category: string) => void;
  setLocation: (location: LocationInput) => void;
  setSelectedOption: (option: "library" | "private") => void;
  setUploadingImages: (isUploading: boolean) => void;
  setIsGeneratingDescription: (isGenerating: boolean) => void;
  setCurrentStepIndex: (index: number) => void;
  reset: () => void;
}

const initialState = {
  title: "",
  description: "",
  duration: "",
  cost: "",
  images: [] as ImageData[],
  categories: [] as string[],
  location: { type: "address" as const, value: "" },
  selectedOption: "library" as const,
  uploadingImages: false,
  isGeneratingDescription: false,
  currentStepIndex: 0,
};

const useCustomActivityStore = create<CustomActivityState>((set) => ({
  ...initialState,

  setTitle: (title) => set({ title }),
  setDescription: (description: string | ((prev: string) => string)) => {
    if (typeof description === "function") {
      set((state) => ({
        description: description(state.description),
      }));
    } else {
      set({ description });
    }
  },
  setDuration: (duration) => set({ duration }),
  setCost: (cost) => set({ cost }),

  addImage: (image) =>
    set((state) => ({
      images: [...state.images, image],
    })),

  removeImage: (index) =>
    set((state) => ({
      images: state.images.filter((_, i) => i !== index),
    })),

  setCategories: (categories) => set({ categories }),

  toggleCategory: (category) =>
    set((state) => {
      const categories = state.categories.includes(category)
        ? state.categories.filter((t) => t !== category)
        : state.categories.length < 3
        ? [...state.categories, category]
        : state.categories;

      return { categories };
    }),

  setLocation: (location) => set({ location }),
  setSelectedOption: (selectedOption) => set({ selectedOption }),
  setUploadingImages: (uploadingImages) => set({ uploadingImages }),
  setIsGeneratingDescription: (isGeneratingDescription) =>
    set({ isGeneratingDescription }),
  setCurrentStepIndex: (currentStepIndex) => set({ currentStepIndex }),

  reset: () => set({ ...initialState, currentStepIndex: 0 }),
}));

export default useCustomActivityStore;
